import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints';

import { Appointment } from '@/types/matchDevice';

export interface AppointmentDetail {
  id?: string;
  type?: string | null;
  scheduledDate?: string | null;
  status?: string | null;
  frequency?: string | null;
  frequencyNumber?: number | null;
  cptCode?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface LinkAppointmentToTransmissionRequest {
  appointmentId: string | null;
  transmissionId: number;
}

interface LinkAppointmentToTransmissionResponse {
  success: boolean;
  message: string;
}

export const appointmentApiSlice = createApi({
  reducerPath: 'appointmentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.Appointment, API_SLICE.ScheduleList],
  endpoints: (builder) => ({
    linkAppointmentToTransmission: builder.mutation<
      LinkAppointmentToTransmissionResponse,
      LinkAppointmentToTransmissionRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.APPOINTMENT.LINK_TRANSMISSION,
        method: 'POST',
        body
      })
    }),

    getActiveAppointments: builder.query<Appointment[], { deviceId: string }>({
      query: ({ deviceId }) => ({
        url: API_ENDPOINTS.APPOINTMENT.ACTIVE(deviceId),
        method: 'GET'
      }),
      transformResponse: (response: { data: Appointment[] }) => response.data,
      providesTags: (result, _error, arg) => [
        { type: API_SLICE.Appointment, id: `device-${arg.deviceId}` },
        ...(result ?? []).map((item) => ({ type: API_SLICE.Appointment, id: item.id }))
      ]
    }),
    updateAppointment: builder.mutation<
      Appointment,
      {
        id: string;
        status?: string;
        scheduledDate?: string;
        type?: string;
      }
    >({
      query: ({ id, status, scheduledDate, type }) => ({
        url: `${API_ENDPOINTS.APPOINTMENT.LIST}/${id}`,
        method: 'PATCH',
        body: {
          ...(status ? { status } : {}),
          ...(scheduledDate ? { scheduledDate } : {}),
          ...(type ? { type } : {})
        }
      }),
      invalidatesTags: (_result, _error, arg) => [
        API_SLICE.ScheduleList,
        { type: API_SLICE.Appointment, id: arg.id }
      ]
    }),
    getAppointment: builder.query<AppointmentDetail, string>({
      query: (id) => ({
        url: `${API_ENDPOINTS.APPOINTMENT.LIST}/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: { appointment?: AppointmentDetail }) =>
        response.appointment ?? {},
      providesTags: (_result, _error, id) => [{ type: API_SLICE.Appointment, id }]
    }),
    listAppointments: builder.query<
      {
        data: {
          id: string;
          status?: string | null;
          scheduledDate?: string | null;
          type?: string | null;
          matchDeviceId?: string | null;
          frequencyNumber?: number | null;
        }[];
        total: number;
      },
      { patientId: string; page?: number; limit?: number; status?: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.APPOINTMENT.LIST,
        method: 'GET',
        params
      }),
      providesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Appointment, id: patientId }
      ]
    }),
    runScheduler: builder.mutation<
      { messages: string[]; schedule: Record<string, string[]> },
      { deviceId: string }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.APPOINTMENT.RUN_SCHEDULER,
        method: 'POST',
        body
      }),
      invalidatesTags: [API_SLICE.ScheduleList]
    }),
    cancelAppointments: builder.mutation<null, { deviceId: string } | { patientId: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.APPOINTMENT.CANCEL_ALL,
        method: 'POST',
        body
      }),
      invalidatesTags: [API_SLICE.ScheduleList]
    }),
    listScheduleAppointments: builder.query<
      { data: unknown[]; total: number; page?: number; limit?: number },
      {
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
        statuses?: string;
        type?: string;
        organizationIds?: string;
        sortOrder?: 'asc' | 'desc';
      }
    >({
      query: ({ startDate, endDate, page, limit, statuses, type, organizationIds, sortOrder }) => ({
        url: API_ENDPOINTS.APPOINTMENT.LIST,
        method: 'GET',
        params: {
          startDate,
          endDate,
          page,
          limit,
          sortOrder: sortOrder ?? 'asc',
          ...(statuses ? { statuses } : {}),
          ...(type ? { type } : {}),
          ...(organizationIds ? { organizationIds } : {})
        }
      }),
      providesTags: [API_SLICE.ScheduleList]
    })
  })
});
export const {
  useGetActiveAppointmentsQuery,
  useLinkAppointmentToTransmissionMutation,
  useUpdateAppointmentMutation,
  useGetAppointmentQuery,
  useLazyListAppointmentsQuery,
  useListAppointmentsQuery,
  useListScheduleAppointmentsQuery,
  useLazyListScheduleAppointmentsQuery,
  useRunSchedulerMutation,
  useCancelAppointmentsMutation,
  useLazyGetAppointmentQuery
} = appointmentApiSlice;
